import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { agentApi, paymentApi } from '../services/api';
import { Agent } from '../types/api';
import GoogleAdBanner from './GoogleAdBanner';
import PaymentModal from './PaymentModal';
import ImageHoverPreview from './ImageHoverPreview';

interface AgentMarketProps {
  isAuthenticated: boolean;
}

// 智能体卡片组件
const AgentCard: React.FC<{
  agent: Agent;
  onClick: () => void;
}> = ({ agent, onClick }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer border border-gray-200 dark:border-gray-700 overflow-hidden group"
    >
      {/* 封面图 */}
      {agent.images && agent.images.length > 0 ? (
        <div className="aspect-video w-full overflow-hidden bg-gray-100 dark:bg-gray-700 relative">
          <img
            src={agent.images[0]}
            alt={agent.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {/* 悬停预览层 */}
          <div className="absolute inset-0 z-10">
            <ImageHoverPreview
              imageUrl={agent.images[0]}
              alt={agent.name}
              thumbnailClassName="w-full h-full opacity-0"
              previewSize={400}
            />
          </div>
        </div>
      ) : (
        <div className="aspect-video w-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
          <i className="fas fa-robot text-4xl text-primary/50"></i>
        </div>
      )}

      <div className="p-4">
        {/* 名称和价格/使用次数 */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1 flex-1">
            {agent.name}
          </h3>
          <div className="flex items-center space-x-1 ml-2">
            {/* 价格徽章 */}
            {agent.price !== undefined && agent.price > 0 ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                <i className="fas fa-tag mr-1"></i>
                ¥{agent.price.toFixed(2)}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                <i className="fas fa-gift mr-1"></i>
                免费
              </span>
            )}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
            <i className="fas fa-rocket mr-1"></i>
            {agent.hotness || 0}
          </span>
          </div>
        </div>

        {/* 行业标签 */}
        {agent.industryTags && agent.industryTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {agent.industryTags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
              >
                {tag}
              </span>
            ))}
            {agent.industryTags.length > 3 && (
              <span className="text-xs text-gray-400">+{agent.industryTags.length - 3}</span>
            )}
          </div>
        )}

        {/* 描述 */}
        {agent.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {agent.description}
          </p>
        )}

        {/* 底部信息 */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center">
            <i className="fas fa-user mr-1"></i>
            {agent.author || '匿名用户'}
          </span>
          <span>
            {new Date(agent.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// 智能体详情弹窗
const AgentDetailModal: React.FC<{
  agent: Agent | null;
  isOpen: boolean;
  onClose: () => void;
  onUsageIncreased?: (id: number, newHotness: number) => void;
  isAuthenticated?: boolean;
}> = ({ agent, isOpen, onClose, onUsageIncreased, isAuthenticated = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPurchased, setIsPurchased] = useState(agent?.purchased || false);

  // 检查购买状态
  useEffect(() => {
    const checkPurchaseStatus = async () => {
      if (!isOpen || !agent || !isAuthenticated || (agent.price || 0) <= 0) {
        return;
      }
      
      try {
        const result = await paymentApi.checkPurchase('agent', agent.id);
        if (result.success && result.data) {
          setIsPurchased(result.data.purchased || result.data.isOwner || result.data.isFree);
        }
      } catch (error) {
        console.error('检查购买状态失败:', error);
      }
    };
    
    checkPurchaseStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agent?.id, isAuthenticated, agent?.price]);

  if (!agent) return null;

  const needPayment = (agent.price || 0) > 0 && !isPurchased;

  const handleOpenAgent = async () => {
    // 如果需要付费
    if (needPayment) {
      if (!isAuthenticated) {
        toast.error('请先登录后再购买');
        return;
      }
      setShowPaymentModal(true);
      return;
    }

    // 先增加使用次数
    try {
      const result = await agentApi.increaseUsage(agent.id);
      if (result.success && result.data && onUsageIncreased) {
        onUsageIncreased(agent.id, result.data.hotness);
      }
    } catch (error) {
      console.error('增加使用次数失败:', error);
    }
    // 打开智能体
    window.open(agent.url, '_blank');
  };

  const handlePaymentSuccess = () => {
    setIsPurchased(true);
    setShowPaymentModal(false);
    toast.success('支付成功！正在打开智能体...');
    // 增加使用次数并打开
    agentApi.increaseUsage(agent.id).then(result => {
      if (result.success && result.data && onUsageIncreased) {
        onUsageIncreased(agent.id, result.data.hotness);
      }
    }).catch(console.error);
    window.open(agent.url, '_blank');
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(agent.url);
    toast.success('链接已复制到剪贴板');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {agent.name}
                </h2>
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <i className="fas fa-user mr-1"></i>
                  <span>{agent.author || '匿名用户'}</span>
                  <span className="mx-2">•</span>
                  <span>{new Date(agent.createdAt).toLocaleDateString()}</span>
                  <span className="mx-2">•</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                    <i className="fas fa-rocket mr-1"></i>
                    {agent.hotness || 0} 次使用
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            {/* 内容 */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* 行业标签 */}
              {agent.industryTags && agent.industryTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {agent.industryTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 描述 */}
              {agent.description && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    <i className="fas fa-info-circle text-primary mr-2"></i>
                    智能体介绍
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {agent.description}
                  </p>
                </div>
              )}

              {/* 效果图片 */}
              {agent.images && agent.images.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    <i className="fas fa-images text-primary mr-2"></i>
                    效果展示
                  </h3>
                  
                  {/* 主图 */}
                  <div className="mb-3 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <img
                      src={agent.images[currentImageIndex]}
                      alt={`效果图 ${currentImageIndex + 1}`}
                      className="w-full h-auto max-h-96 object-contain bg-gray-50 dark:bg-gray-900"
                    />
                  </div>
                  
                  {/* 缩略图 */}
                  {agent.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {agent.images.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentImageIndex
                              ? 'border-primary'
                              : 'border-transparent hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 链接信息 - 仅免费或已购买时显示 */}
              {!needPayment && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <i className="fas fa-link text-primary mr-2"></i>
                    智能体链接
                  </h3>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 truncate">
                      {agent.url}
                    </code>
                    <button
                      onClick={handleCopyUrl}
                      className="p-2 text-gray-500 hover:text-primary transition-colors"
                      title="复制链接"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 底部 */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                关闭
              </button>
              <button
                onClick={handleOpenAgent}
                className={`px-6 py-2 rounded-lg transition-colors flex items-center ${
                  needPayment 
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                    : 'bg-primary hover:bg-primary-dark text-white'
                }`}
              >
                {needPayment ? (
                  <>
                    <i className="fas fa-lock mr-2"></i>
                    支付 ¥{(agent.price || 0).toFixed(2)} 使用
                  </>
                ) : (
                  <>
                <i className="fas fa-external-link-alt mr-2"></i>
                打开智能体
                  </>
                )}
              </button>
            </div>
          </motion.div>
          
          {/* 支付弹窗 */}
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={handlePaymentSuccess}
            itemType="agent"
            itemId={agent.id}
            itemName={agent.name}
            price={agent.price || 0}
            sellerName={agent.author}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// 主组件
const AgentMarket: React.FC<AgentMarketProps> = ({ isAuthenticated }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [industryTags, setIndustryTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'created_at' | 'hotness'>('created_at');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const LIMIT = 12;

  // 加载智能体（用于加载更多）
  const loadMore = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const result = await agentApi.getMarket({
        search: searchQuery || undefined,
        limit: LIMIT,
        offset: offset,
        sortBy,
        industry_tag: selectedTag || undefined
      }) as any;

      if (result.success) {
        const newAgents = result.data || [];
        setAgents(prev => [...prev, ...newAgents]);
        setHasMore(result.hasMore || false);
        setTotal(result.total || 0);
        if (result.industryTags) {
          setIndustryTags(result.industryTags);
        }
        setOffset(prev => prev + LIMIT);
      }
    } catch (error) {
      console.error('加载智能体失败:', error);
      toast.error('加载失败，请重试');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedTag, sortBy, offset]);

  // 初始加载和筛选条件变化时重置
  useEffect(() => {
    const loadInitial = async () => {
      try {
        setIsLoading(true);
        setOffset(0);
        
        const result = await agentApi.getMarket({
          search: searchQuery || undefined,
          limit: LIMIT,
          offset: 0,
          sortBy,
          industry_tag: selectedTag || undefined
        }) as any;

        if (result.success) {
          setAgents(result.data || []);
          setHasMore(result.hasMore || false);
          setTotal(result.total || 0);
          if (result.industryTags) {
            setIndustryTags(result.industryTags);
          }
          setOffset(LIMIT);
        }
      } catch (error) {
        console.error('加载智能体失败:', error);
        toast.error('加载失败，请重试');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitial();
  }, [searchQuery, selectedTag, sortBy]);

  // 搜索防抖
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 pb-12">
      <div className="container mx-auto px-4 sm:px-6">
        {/* 页面标题 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            <i className="fas fa-robot text-primary mr-3"></i>
            TRAE 智能体市场
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            发现和分享优秀的 TRAE 智能体，一键复刻使用
          </p>
        </div>

        {/* 搜索和筛选 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* 搜索框 */}
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="搜索智能体名称或描述..."
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>

            {/* 排序 */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as 'created_at' | 'hotness')}
              className="px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary"
            >
              <option value="created_at">最新发布</option>
              <option value="hotness">最受欢迎</option>
            </select>
          </div>

          {/* 行业标签筛选 */}
          {industryTags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedTag('')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedTag === ''
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                全部
              </button>
              {industryTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedTag === tag
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 统计信息 */}
        <div className="flex items-center justify-between mb-4 text-sm text-gray-600 dark:text-gray-400">
          <span>共 {total} 个智能体</span>
        </div>

        {/* 智能体列表 */}
        {isLoading && agents.length === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-20">
            <i className="fas fa-robot text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <p className="text-gray-500 dark:text-gray-400">暂无智能体</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {agents.map(agent => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  onClick={() => setSelectedAgent(agent)}
                />
              ))}
            </div>

            {/* 广告位 */}
            <div className="mt-8">
              <GoogleAdBanner slot="YOUR_AGENT_MARKET_AD_SLOT" format="horizontal" />
            </div>

            {/* 加载更多 */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={loadMore}
                  disabled={isLoading}
                  className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      加载中...
                    </>
                  ) : (
                    '加载更多'
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* 详情弹窗 */}
        <AgentDetailModal
          agent={selectedAgent}
          isOpen={!!selectedAgent}
          onClose={() => setSelectedAgent(null)}
          isAuthenticated={isAuthenticated}
          onUsageIncreased={(id, newHotness) => {
            // 更新列表中对应智能体的使用次数
            setAgents(prev => prev.map(a => 
              a.id === id ? { ...a, hotness: newHotness } : a
            ));
            // 同时更新选中的智能体
            if (selectedAgent && selectedAgent.id === id) {
              setSelectedAgent({ ...selectedAgent, hotness: newHotness });
            }
          }}
        />
      </div>
    </div>
  );
};

export default AgentMarket;

